import type { Lifecycle, Money, OrderItem } from "./types";

export { formatCurrencyAmount as money } from "@/shared/lib/money";

/** Satang → "฿1,250.50". Only for satang-typed view-models (currently splits). Returns, orders list/detail use money() on baht. */
export function baht(satang: number): string {
  return (
    "฿" +
    (satang / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  );
}

export function itemCount(items: Pick<OrderItem, "qty">[] | null | undefined): number {
  return (items ?? []).reduce((s, i) => s + i.qty, 0);
}

/** Ship-by deadline minus now, in ms (negative = overdue). `now` defaults to Date.now() so callers (components) never call the impure clock directly. */
export function shipByDiffMs(iso: string, now: number = Date.now()): number {
  return new Date(iso).getTime() - now;
}

/**
 * Whole-hours (or whole-days, once the magnitude reaches 48h) breakdown of a
 * millisecond duration, for the Ship-by column's relative-time display.
 * Sign-agnostic — callers decide "in" vs "overdue" from the duration's sign.
 */
export function formatRelativeHours(ms: number): { amount: number; unit: "h" | "d" } {
  const hours = Math.round(Math.abs(ms) / 3_600_000);
  if (hours >= 48) return { amount: Math.round(Math.abs(ms) / 86_400_000), unit: "d" };
  return { amount: hours, unit: "h" };
}

const BANGKOK_TZ = "Asia/Bangkok";
const bangkokDateFormatter = new Intl.DateTimeFormat("en-GB", {
  timeZone: BANGKOK_TZ,
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});
const bangkokTimeFormatter = new Intl.DateTimeFormat("en-GB", {
  timeZone: BANGKOK_TZ,
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

/**
 * Formats an ISO-8601 timestamp as DD/MM/YYYY HH:mm in Asia/Bangkok
 * wall-clock time, regardless of the input's own UTC offset or the
 * viewer's local timezone — all Atlas OMS order timestamps are shown in
 * Bangkok time for a consistent read across the ops team. Bangkok has no
 * DST, so the GMT+7 offset is a fixed constant, not derived per-date.
 */
export function formatBangkokDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${bangkokDateFormatter.format(d)} ${bangkokTimeFormatter.format(d)} GMT+7`;
}

export type MktStatus = "AWAITING_SHIPMENT" | "SHIPPED" | "COMPLETED" | "CANCELLED" | "RETURNED";

export function mktStatus(s: Lifecycle): MktStatus {
  if (s === "cancelled") return "CANCELLED";
  if (s === "returned") return "RETURNED";
  if (s === "delivered") return "COMPLETED";
  if (s === "shipped") return "SHIPPED";
  return "AWAITING_SHIPMENT";
}

/** Title-cases a raw channel/status slug for display, e.g. "awaiting_shipment" -> "Awaiting Shipment". */
export function titleCase(s: string): string {
  return s
    .split(/[\s_]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

const EPS = 0.011;

/**
 * Buyer-side lines for the Order Totals card. `lineTotalSum` is Σ item.total
 * over non-gift lines, computed by the caller from the rows it already has.
 * Per TC136, item.total already nets out the *item-level* discount (item.total
 * = selling price − item discount) but never the order-level voucher — so the
 * voucher must be subtracted separately below, not folded into lineTotalSum.
 * `reconciles` is the ingest invariant seen from the client:
 *   sub − totalDisc + shipping ≈ grand   AND   lineTotalSum − voucher + shipping ≈ grand.
 */
export function totals(m: Money, lineTotalSum: number) {
  const itemDisc = m.item_discount;
  const voucher = m.discount;
  const totalDisc = itemDisc + voucher;
  const sub = m.original;
  const grand = m.total;
  const shipping = m.shipping;
  const reconciles =
    Math.abs(sub - totalDisc + shipping - grand) < EPS &&
    Math.abs(lineTotalSum - voucher + shipping - grand) < EPS;
  return { sub, itemDisc, voucher, totalDisc, shipping, grand, reconciles };
}
